/**
 * GitHubConnectionForm — "Add GitHub Connection".
 *
 * Registers a GitHub connection (org/user + installation credential) so LeftShield can scan its
 * repositories. `POST` via `useCreateGitHubConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `GitHubConnection.schema.ts`
 * (never redefined), Fluent `Field` for verbatim validation messages, server errors mapped via
 * the shared `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog`
 * chrome is owned by `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`)
 * — this component renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel`
 * prop contract so the hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { PlugConnectedAddRegular } from '@fluentui/react-icons';
import { useCreateGitHubConnection } from '../hooks';
import type { CreateGitHubConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { GITHUB_INSTALLATION_TYPE_OPTIONS } from './connectionFormFields';
import {
  GitHubConnectionSchema,
  type GitHubConnectionFormValues,
  type GitHubConnectionFormOutput,
} from './GitHubConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'installationType', 'organizationOrUser', 'accessToken'] as const;

const DEFAULT_VALUES: GitHubConnectionFormValues = {
  name: '',
  installationType: 'PersonalAccessToken',
  organizationOrUser: '',
  accessToken: '',
};

export interface GitHubConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const GitHubConnectionForm: FC<GitHubConnectionFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<GitHubConnectionFormValues, unknown, GitHubConnectionFormOutput>({
    resolver: zodResolver(GitHubConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const installationType = watch('installationType');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateGitHubConnection();

  const onValid = (values: GitHubConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateGitHubConnectionRequest = {
      name: values.name,
      installationType: values.installationType,
      organizationOrUser: values.organizationOrUser,
      accessToken: values.accessToken,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="github-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="github-connection-form-name"
              value={field.value ?? ''}
              placeholder="My GitHub Organization"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Organization or Username"
        required
        validationState={errors.organizationOrUser ? 'error' : undefined}
        validationMessage={errors.organizationOrUser?.message}
        hint="The GitHub organization name or username that owns the repositories to scan."
      >
        <Controller
          name="organizationOrUser"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="github-connection-form-organizationOrUser"
              value={field.value ?? ''}
              placeholder="my-organization"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field label="Authentication Type" required>
        <Controller
          name="installationType"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={createConnection.isPending}
              data-testid="github-connection-form-installationType"
            >
              {GITHUB_INSTALLATION_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      <Field
        label={installationType === 'PersonalAccessToken' ? 'Personal Access Token' : 'Access Token'}
        required
        validationState={errors.accessToken ? 'error' : undefined}
        validationMessage={errors.accessToken?.message}
        hint={
          installationType === 'PersonalAccessToken'
            ? 'Classic PAT requires repo, read:org, and admin:repo_hook scopes. Fine-grained tokens recommended.'
            : installationType === 'GitHubApp'
            ? 'GitHub App installation token or private key.'
            : 'OAuth token with repository access.'
        }
      >
        <Controller
          name="accessToken"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="github-connection-form-accessToken"
              value={field.value ?? ''}
              placeholder={
                installationType === 'PersonalAccessToken'
                  ? 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
                  : 'Token or key'
              }
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="github-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<PlugConnectedAddRegular />}
      />
    </form>
  );
};
